import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  CreditCard,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { formatCurrency } from '@/lib/utils';
import {
  RECORD_PAYMENT,
  GET_INVOICE,
  GET_INVOICES_BY_ORGANIZATION,
  GET_STATEMENTS_BY_ORGANIZATION,
  type PaymentMethod,
} from '@/graphql/invoices';

// ==================== Types ====================

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string | null;
  invoiceNumber: string;
  balanceDue: number;
  clientId: string;
}

const PAYMENT_METHODS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'ACH', label: 'ACH' },
  { value: 'WIRE', label: 'Wire Transfer' },
  { value: 'CHECK', label: 'Check' },
  { value: 'OTHER', label: 'Other' },
];

// ==================== Helpers ====================

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

// ==================== Component ====================

export function RecordPaymentModal({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
  balanceDue,
  clientId,
}: RecordPaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  // Mutation
  const [recordPayment, { loading }] = useMutation(RECORD_PAYMENT, {
    refetchQueries: [
      { query: GET_INVOICE, variables: { id: invoiceId } },
      { query: GET_INVOICES_BY_ORGANIZATION, variables: { organizationId: clientId } },
      { query: GET_STATEMENTS_BY_ORGANIZATION, variables: { organizationId: clientId } },
    ],
  });

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setAmount(balanceDue > 0 ? balanceDue.toFixed(2) : '');
      setPaymentDate(todayISO());
      setPaymentMethod('');
      setReferenceNumber('');
      setNotes('');
      setErrors([]);
    }
  }, [isOpen, balanceDue]);

  // ==================== Validation ====================

  function validate(): string[] {
    const errs: string[] = [];
    const parsedAmount = parseFloat(amount);

    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      errs.push('Payment amount must be greater than $0.00.');
    }
    if (parsedAmount > balanceDue) {
      errs.push(`Payment amount ($${parsedAmount.toFixed(2)}) exceeds balance due (${formatCurrency(balanceDue)}).`);
    }
    if (!paymentDate) {
      errs.push('Payment date is required.');
    }
    if (!paymentMethod) {
      errs.push('Payment method is required.');
    }
    return errs;
  }

  // ==================== Submit ====================

  async function handleSubmit() {
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);

    try {
      const input = {
        invoiceId,
        amount: parseFloat(amount),
        paymentDate,
        paymentMethod,
        referenceNumber: referenceNumber.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      await recordPayment({ variables: { input } });
      onClose();
    } catch (err: unknown) {
      setErrors([err instanceof Error ? err.message : 'Failed to record payment.']);
    }
  }

  // ==================== Render ====================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record Payment"
      size="md"
      footer={
        <>
          <button onClick={onClose} disabled={loading} className="btn-ghost px-4 py-2 text-sm">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading} className="btn-primary px-4 py-2 text-sm">
            {loading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" />
            ) : (
              <CreditCard className="w-4 h-4 mr-1.5 inline" />
            )}
            Record Payment
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-rust/10 dark:bg-salmon/10 border border-rust/20 dark:border-salmon/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rust dark:text-salmon mt-0.5 flex-shrink-0" />
              <ul className="text-sm text-rust dark:text-salmon space-y-0.5">
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Invoice Summary */}
        <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Invoice</p>
              <p className="font-medium text-slate dark:text-mist">{invoiceNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Balance Due</p>
              <p className="text-lg font-semibold text-earth dark:text-yellow-light">
                {formatCurrency(balanceDue)}
              </p>
            </div>
          </div>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
            Payment Amount <span className="text-rust">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-chalkboard/50 dark:text-rain-cloud/50 text-sm">
              $
            </span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={balanceDue}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="input w-full pl-7"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Date + Method */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
              Payment Date <span className="text-rust">*</span>
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
              Payment Method <span className="text-rust">*</span>
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
              className="select w-full"
            >
              <option value="">Select method...</option>
              {PAYMENT_METHODS.map((pm) => (
                <option key={pm.value} value={pm.value}>
                  {pm.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Reference Number */}
        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
            Reference Number
          </label>
          <input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            className="input w-full"
            placeholder="Check #, wire ref, etc."
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="textarea w-full"
            placeholder="Optional payment notes..."
          />
        </div>
      </div>
    </Modal>
  );
}
